import type { CustomField } from '../api/types';

/**
 * Renders one admin-defined field.
 *
 * The whole point of the custom-field system is that this component has never
 * heard of "School Name" or "Student Class" — it renders whatever the registry
 * describes. Nothing here may be keyed off a specific api_key.
 */
export function CustomFieldInput({
  field,
  value,
  onChange,
  error,
}: {
  field: CustomField;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
}) {
  const id = `cf-${field.api_key}`;
  const describedBy = [field.help_text ? `${id}-help` : null, error ? `${id}-err` : null]
    .filter(Boolean)
    .join(' ');

  // Retired options must never be selectable, but a lead already holding one
  // keeps it — so the current value is added back if it is no longer active.
  const selectable = field.options.filter(
    (o) => o.is_active || o.value === value || (Array.isArray(value) && value.includes(o.value)),
  );

  const common = {
    id,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy || undefined,
  };

  let control: JSX.Element;

  switch (field.field_type) {
    case 'select':
      control = (
        <select {...common} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">Select…</option>
          {selectable.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
              {!o.is_active ? ' (retired)' : ''}
            </option>
          ))}
        </select>
      );
      break;

    case 'radio':
      control = (
        <div role="radiogroup" aria-labelledby={`${id}-label`} style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
          {selectable.map((o) => (
            <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 400, marginBottom: 0 }}>
              <input
                type="radio"
                name={id}
                value={o.value}
                checked={value === o.value}
                onChange={() => onChange(o.value)}
                style={{ width: 'auto' }}
              />
              {o.label}
            </label>
          ))}
        </div>
      );
      break;

    case 'multi_select': {
      const selected = new Set(Array.isArray(value) ? (value as string[]) : []);
      control = (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px' }}>
          {selectable.map((o) => (
            <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 400, marginBottom: 0 }}>
              <input
                type="checkbox"
                checked={selected.has(o.value)}
                onChange={(e) => {
                  const next = new Set(selected);
                  if (e.target.checked) next.add(o.value);
                  else next.delete(o.value);
                  // Send null rather than [] so the server clears the key
                  // instead of storing an empty array.
                  onChange(next.size > 0 ? [...next] : null);
                }}
                style={{ width: 'auto' }}
              />
              {o.label}
            </label>
          ))}
        </div>
      );
      break;
    }

    case 'long_text':
      control = (
        <textarea
          {...common}
          value={(value as string) ?? ''}
          maxLength={field.validation.max_length ?? 20000}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
      break;

    case 'number':
    case 'decimal':
      control = (
        <input
          {...common}
          type="number"
          inputMode={field.field_type === 'decimal' ? 'decimal' : 'numeric'}
          step={field.field_type === 'decimal' ? 1 / 10 ** (field.validation.decimals ?? 2) : 1}
          min={field.validation.min}
          max={field.validation.max}
          value={(value as number | string) ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
      break;

    case 'boolean':
    case 'checkbox':
      control = (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400, marginBottom: 0 }}>
          <input
            {...common}
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            style={{ width: 'auto' }}
          />
          <span style={{ fontSize: 13.5 }}>{field.help_text ?? 'Yes'}</span>
        </label>
      );
      break;

    case 'date':
      control = (
        <input {...common} type="date" value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value || null)} />
      );
      break;

    case 'datetime':
      control = (
        <input
          {...common}
          type="datetime-local"
          // The API returns full ISO; datetime-local wants YYYY-MM-DDTHH:mm.
          value={value ? String(value).slice(0, 16) : ''}
          onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
        />
      );
      break;

    default: {
      const inputType =
        { email: 'email', phone: 'tel', url: 'url' }[field.field_type as string] ?? 'text';
      control = (
        <input
          {...common}
          type={inputType}
          maxLength={field.validation.max_length}
          placeholder={field.field_type === 'phone' ? '98765 43210' : undefined}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
    }
  }

  const isBooleanish = field.field_type === 'boolean' || field.field_type === 'checkbox';

  return (
    <div className={`field${error ? ' invalid' : ''}`}>
      {!isBooleanish && (
        <label htmlFor={id} id={`${id}-label`}>
          {field.label}
          {field.is_required && <span className="req" aria-hidden="true">*</span>}
        </label>
      )}
      {control}
      {field.help_text && !isBooleanish && (
        <div className="hint" id={`${id}-help`}>
          {field.help_text}
        </div>
      )}
      {error && (
        <div className="err" id={`${id}-err`}>
          {error}
        </div>
      )}
    </div>
  );
}

/** Renders every active field in registry order, grouped where configured. */
export function CustomFieldSet({
  fields,
  values,
  onChange,
  errors = {},
}: {
  fields: CustomField[];
  values: Record<string, unknown>;
  onChange: (apiKey: string, value: unknown) => void;
  errors?: Record<string, string>;
}) {
  const groups = new Map<string, CustomField[]>();
  for (const field of fields) {
    if (!field.is_active) continue;
    const key = field.field_group ?? '';
    const list = groups.get(key) ?? [];
    list.push(field);
    groups.set(key, list);
  }

  return (
    <>
      {[...groups.entries()].map(([group, groupFields]) => (
        <div key={group || 'ungrouped'}>
          {group && (
            <h4 style={{ margin: '18px 0 10px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>
              {group}
            </h4>
          )}
          <div className="grid-2">
            {groupFields.map((field) => (
              <CustomFieldInput
                key={field.api_key}
                field={field}
                value={values[field.api_key]}
                onChange={(v) => onChange(field.api_key, v)}
                error={errors[field.api_key]}
              />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}
